/* global XLSX */
export const exportToExcel = (data, fileName) => {
    if (typeof XLSX === 'undefined') {
        console.error("XLSX library is not loaded.");
        return;
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

export const formatCurrency = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0 ₫';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
};

export const formatNumber = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0';
    return value.toLocaleString('en-US');
};

export const parseFormattedNumber = (str) => {
    if (typeof str !== 'string') return 0;
    const num = parseInt(str.replace(/,/g, ''), 10);
    return isNaN(num) ? 0 : num;
};

export const getLocalDateString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    return dateString.split('T')[0];
};

export const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) throw new Error("Invalid date");
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        return dateString;
    }
};

export const parseAttachmentUrls = (urlField) => {
    if (!urlField) return [];
    if (Array.isArray(urlField)) return urlField;
    if (typeof urlField !== 'string') return [];
    
    try {
        const parsed = JSON.parse(urlField);
        return Array.isArray(parsed) ? parsed : [urlField];
    } catch (e) {
        return [urlField];
    }
};

export const calculateItemCost = (item) => {
    if (item.purchasePrice !== undefined) {
         const purchasePrice = item.purchasePrice || 0;
         const discount = item.discount || 0;
         const discountPercentage = Math.max(0, Math.min(100, discount));
         return purchasePrice * (1 - discountPercentage / 100);
    }
    return item.cost || 0;
};

export const calculateOrderProfit = (order) => {
    if (!order || !Array.isArray(order.items)) {
        return { profit: 0, percentage: 0 };
    }
    if (order.label === 'Góp vốn hàng (Daklak)' || order.label === 'Góp vốn hàng (HCM)') {
        return { profit: null, percentage: null };
    }
    const itemsTotal = order.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
    
    const totalCostOfGoods = order.items.reduce((sum, item) => {
        const cost = calculateItemCost(item);
        return sum + cost * (item.quantity || 0);
    }, 0);
    
    const profit = (itemsTotal + (order.shippingFee || 0)) - (totalCostOfGoods + (order.realShippingFee || 0)) - ((order.promotion || 0) + (order.platformFee || 0));
    const customerPaid = itemsTotal + (order.shippingFee || 0) - (order.promotion || 0);
    const percentage = customerPaid > 0 ? (profit / customerPaid) * 100 : 0;
    return { profit, percentage };
};

export const removeAccents = (str) => {
    if (!str) return '';
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");
};

// *** MỚI: Các hàm xử lý tồn kho theo vị trí ***

/**
 * Phân tích chuỗi meta tồn kho thành một mảng các đối tượng có cấu trúc.
 * @param {Array} metaData - Mảng meta_data từ sản phẩm.
 * @returns {Array} - Mảng các đối tượng tồn kho, ví dụ: [{ id, kho, so_luong, vi_tri }]
 */
export const parseInventoryLocations = (metaData) => {
    const locationKeys = ['ton_kho_dl', 'ton_kho_hcm', 'ton_kho_khac'];
    let allLocations = [];
    let idCounter = 0;

    if (!Array.isArray(metaData)) {
        return [];
    }

    locationKeys.forEach(key => {
        const metaField = metaData.find(m => m.key === key);
        if (metaField && metaField.value) {
            const entries = metaField.value.split(',').map(s => s.trim()).filter(Boolean);
            entries.forEach(entry => {
                const parts = entry.split('-');
                if (parts.length >= 3) {
                    allLocations.push({
                        id: idCounter++,
                        kho: parts[0],
                        so_luong: parts[1],
                        vi_tri: parts.slice(2).join('-') // Gộp lại các phần còn lại cho vị trí
                    });
                }
            });
        }
    });

    return allLocations;
};

/**
 * Tổng hợp mảng các đối tượng tồn kho thành các chuỗi meta để lưu.
 * @param {Array} locations - Mảng các đối tượng tồn kho từ form chỉnh sửa.
 * @returns {Object} - Đối tượng chứa các chuỗi meta đã được định dạng, ví dụ: { ton_kho_dl: 'DL-1-A1,DL-2-A2' }
 */
export const consolidateInventoryLocations = (locations) => {
    const grouped = {
        ton_kho_dl: [],
        ton_kho_hcm: [],
        ton_kho_khac: []
    };

    locations.forEach(loc => {
        if (!loc.kho || !loc.so_luong || !loc.vi_tri) return;

        const entryString = `${loc.kho}-${loc.so_luong}-${loc.vi_tri}`;
        
        if (loc.kho.toLowerCase().includes('dl')) {
            grouped.ton_kho_dl.push(entryString);
        } else if (loc.kho.toLowerCase().includes('hcm')) {
            grouped.ton_kho_hcm.push(entryString);
        } else {
            grouped.ton_kho_khac.push(entryString);
        }
    });

    return {
        ton_kho_dl: grouped.ton_kho_dl.join(','),
        ton_kho_hcm: grouped.ton_kho_hcm.join(','),
        ton_kho_khac: grouped.ton_kho_khac.join(',')
    };
};
